import isString from 'lodash/isString';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import uuid from 'uuid';

import {
  FACILITY_ROLE,
  FACILITY_STAFF_ROLE,
  REPAIRER_ROLE,
  STICKY_FACILITY_FILTER_SETTING,
  SHOULD_SENT_SMS,
} from '@uptime/shared/constants';
import { DAYS_OPTIONS, NOTIFICATION_LABELS, WIZARD_PAGES } from '@uptime/shared/constants/account';

// TODO: should be moved to general
import {
  business,
  documents,
  documentType,
  notifications,
  personal,
  team,
  teamList,
} from '../components/Account/wizardSteps';

import type { ReactElement } from 'react';

interface WizardStep {
  stepLabel: string;
  icon: ReactElement;
  step: string;
}

interface Permissions {
  facilityUsersManagementPermission: boolean;
  techAccountManagementPermission: boolean;
  [key: string]: unknown;
}

interface Setting {
  title: string;
  [key: string]: unknown;
}

interface ProfileData {
  firstName?: string;
  lastName?: string;
  mobile?: string;
  step?: number;
  image?: unknown;
  businessName?: string;
}

interface DataWithNotification {
  notification: Setting[];
  [key: string]: unknown;
}

export const getStepsForRole = (
  role: string | null,
  permissions: Permissions,
  isDocumentManager: boolean,
  hasConfigurableSettings: boolean
): [WizardStep[], Partial<typeof WIZARD_PAGES>, Partial<typeof WIZARD_PAGES>, WizardStep[]] => {
  const { facilityUsersManagementPermission, techAccountManagementPermission } = permissions;
  const isTechSub = role === REPAIRER_ROLE && !techAccountManagementPermission;

  if (isTechSub) {
    return [
      // wizard steps
      [personal],
      // elements/steps
      pick(WIZARD_PAGES, [WIZARD_PAGES.PERSONAL_INFORMATION]),
      // wizard steps
      pick(WIZARD_PAGES, [WIZARD_PAGES.PERSONAL_INFORMATION]),
      // account edit headers
      [personal],
    ];
  }

  switch (role) {
    case FACILITY_ROLE:
      return [
        // wizard headers
        [
          personal,
          business,
          ...(facilityUsersManagementPermission ? [team] : []),
          ...(hasConfigurableSettings ? [notifications] : []),
        ],
        // elements/steps
        pick(WIZARD_PAGES, [
          WIZARD_PAGES.PERSONAL_INFORMATION,
          WIZARD_PAGES.BUSINESS_INFORMATION,
          ...(facilityUsersManagementPermission ? [WIZARD_PAGES.TEAM] : []),
          ...(isDocumentManager ? [WIZARD_PAGES.DOCUMENT_TYPES] : []),
          ...(hasConfigurableSettings ? [WIZARD_PAGES.NOTIFICATIONS] : []),
          WIZARD_PAGES.TEAM_LIST,
        ]),
        // wizard steps
        pick(WIZARD_PAGES, [
          WIZARD_PAGES.PERSONAL_INFORMATION,
          WIZARD_PAGES.BUSINESS_INFORMATION,
          ...(facilityUsersManagementPermission ? [WIZARD_PAGES.TEAM] : []),
          ...(hasConfigurableSettings ? [WIZARD_PAGES.NOTIFICATIONS] : []),
        ]),
        // account edit headers
        [
          personal,
          business,
          ...(facilityUsersManagementPermission ? [team] : []),
          ...(isDocumentManager ? [documentType] : []),
          ...(hasConfigurableSettings ? [notifications] : []),
          teamList,
        ],
      ];
    case REPAIRER_ROLE:
      return [
        // wizard headers
        [personal, business, documents, ...(hasConfigurableSettings ? [notifications] : [])],
        // elements/steps
        pick(WIZARD_PAGES, [
          WIZARD_PAGES.PERSONAL_INFORMATION,
          WIZARD_PAGES.BUSINESS_INFORMATION,
          WIZARD_PAGES.DOCUMENTS,
          ...(hasConfigurableSettings ? [WIZARD_PAGES.NOTIFICATIONS] : []),
        ]),
        // wizard steps
        pick(WIZARD_PAGES, [
          WIZARD_PAGES.PERSONAL_INFORMATION,
          WIZARD_PAGES.BUSINESS_INFORMATION,
          WIZARD_PAGES.DOCUMENTS,
          ...(hasConfigurableSettings ? [WIZARD_PAGES.NOTIFICATIONS] : []),
        ]),
        // account edit headers
        [personal, business, documents, ...(hasConfigurableSettings ? [notifications] : [])],
      ];
    case FACILITY_STAFF_ROLE:
    default:
      return [
        // wizard headers
        [personal, ...(hasConfigurableSettings ? [notifications] : [])],
        // elements/steps
        pick(WIZARD_PAGES, [
          WIZARD_PAGES.PERSONAL_INFORMATION,
          ...(isDocumentManager ? [WIZARD_PAGES.DOCUMENT_TYPES] : []),
          ...(hasConfigurableSettings ? [WIZARD_PAGES.NOTIFICATIONS] : []),
        ]),
        // wizard steps
        pick(WIZARD_PAGES, [
          WIZARD_PAGES.PERSONAL_INFORMATION,
          ...(hasConfigurableSettings ? [WIZARD_PAGES.NOTIFICATIONS] : []),
        ]),
        // account edit headers
        [
          personal,
          ...(isDocumentManager ? [documentType] : []),
          ...(hasConfigurableSettings ? [notifications] : []),
        ],
      ];
  }
};

export const getNormalisedTextForSettings = (key: string): string =>
  (NOTIFICATION_LABELS as Record<string, string>)[key] || key;

export const getPreparedSettings = (
  data: Record<string, Setting[]> | null | undefined
): Omit<Setting, 'title'>[] => {
  if (data) {
    const values = Object.values(data);
    const settings = values.reduce<Setting[]>((accumulator, current) => accumulator.concat(current), []);

    return settings.map((item) => omit(item, 'title'));
  }
  return [];
};

export const getImageToSave = (file: File | null | undefined) =>
  file && {
    id: uuid.v4(),
    file,
  };

export const mapProfileData = (data: ProfileData) => ({
  ...pick(data, ['firstName', 'lastName', 'mobile', 'step']),
  ...(data.image ? { image: null } : {}),
  ...(data.step ? { step: data.step } : {}),
  ...(data.businessName ? { businessName: data.businessName } : {}),
});

export const notificationsMapper = (isManager = false, isTopLevelRepairer = false) => {
  const defaultNotifications = isManager
    ? {}
    : {
        emailDeviceAssigned: true,
        isMpSearchable: true,
      };

  return {
    ...defaultNotifications,
    emailCurrentMonthPm: true,
    emailDownDevice: true,
    emailMonthlyDeviceMaintenance: true,
    emailDailyRunTask: true,
    emailDailyRunTaskOverdue: true,
    runTaskReportDaily: true,
    dailyTaskCompletionReportForManager: true,
    dailyTaskOverdueReportForSubAccounts: !isManager,
    dailyTaskOverdueReportForManager: true,
    initialsRequiredToCompleteTasks: true,
    poRequiredForServiceRequests: true,
    allowToShareWONotes: isTopLevelRepairer,
  };
};

export const getDaysLabel = (days: (string | number)[] = []): string => {
  const dayNames = days
    .map((item, index) => {
      if (isString(item)) {
        const day = DAYS_OPTIONS.find(({ value }) => value === item);

        return day && day.label;
      }

      return DAYS_OPTIONS[index] && DAYS_OPTIONS[index].label;
    })
    .filter((item) => Boolean(item));

  return dayNames.join(', ');
};

export const checkIsNotEmptySettings = (settings: Setting[] = []): boolean =>
  settings.filter(({ title }) => title !== STICKY_FACILITY_FILTER_SETTING).length > 0;

export const removeShouldSendSMS = (data: DataWithNotification): DataWithNotification => {
  const newData = Object.assign({}, data);
  newData.notification = newData?.notification.filter(({ title }) => title !== SHOULD_SENT_SMS);

  return newData;
};

export const checkIfHasConfigurableSettings = (settings: Setting[] = []): boolean => {
  return (
    settings.filter(({ title }) => {
      return ![STICKY_FACILITY_FILTER_SETTING, SHOULD_SENT_SMS].includes(title);
    }).length > 0
  );
};
